import { getApiBaseUrl } from '../config/api';

function getAuthHeaders() {
  const token = localStorage.getItem('adminToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function readJson(response, fallbackMessage) {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || body.message || fallbackMessage);
  }
  return response.json();
}

const API_BASE_URL = () => getApiBaseUrl();

export async function fetchAnalyticsSummary(filters = {}) {
  const params = new URLSearchParams();
  ['coverType', 'agent', 'status', 'dateFrom', 'dateTo'].forEach((key) => {
    const val = filters[key];
    if (val && val !== 'all' && val !== '') params.set(key, val);
  });
  const qs = params.toString();
  const response = await fetch(
    `${API_BASE_URL()}/api/admin/analytics/summary${qs ? `?${qs}` : ''}`,
    { headers: getAuthHeaders() }
  );
  const result = await readJson(response, 'Failed to fetch analytics summary');
  return result.data || result;
}

export async function fetchAgentTransactionsAnalytics({
  search = '',
  page = 1,
  pageSize = 25,
  exportAll = false,
} = {}) {
  const params = new URLSearchParams();
  if (search.trim()) params.set('search', search.trim());
  if (exportAll) {
    params.set('exportAll', 'true');
  } else {
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
  }
  const response = await fetch(
    `${API_BASE_URL()}/api/admin/analytics/agent-transactions?${params}`,
    { headers: getAuthHeaders() }
  );
  const result = await readJson(response, 'Failed to fetch agent transactions');
  return result.data || result;
}

export async function fetchUnsynchronizedPolicies({
  search = '',
  page = 1,
  pageSize = 25,
  exportAll = false,
} = {}) {
  const params = new URLSearchParams();
  if (search.trim()) params.set('search', search.trim());
  if (exportAll) {
    params.set('exportAll', 'true');
  } else {
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
  }
  const response = await fetch(
    `${API_BASE_URL()}/api/admin/unsynchronized-policies?${params}`,
    { headers: getAuthHeaders() }
  );
  const result = await readJson(response, 'Failed to fetch unsynchronized policies');
  return result.data || result;
}

export async function fetchProductRatesAnalytics({ coverType = 'all' } = {}) {
  const params = new URLSearchParams();
  if (coverType && coverType !== 'all') params.set('coverType', coverType);
  const qs = params.toString();
  const response = await fetch(
    `${API_BASE_URL()}/api/admin/product-rates${qs ? `?${qs}` : ''}`,
    { headers: getAuthHeaders() }
  );
  const result = await readJson(response, 'Failed to fetch product rates');
  return result;
}

export async function fetchUnderwritingAnalytics() {
  const response = await fetch(`${API_BASE_URL()}/api/admin/underwriting/analytics`, {
    headers: getAuthHeaders(),
  });
  const result = await readJson(response, 'Failed to fetch underwriting analytics');
  return result.data || result;
}

export async function retryCoreSubmission({ clientId, referenceNumber } = {}) {
  const response = await fetch(`${API_BASE_URL()}/api/admin/recovery/step-core`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ clientId, referenceNumber: referenceNumber || null }),
  });
  const result = await readJson(response, 'Core submission retry failed');
  return result;
}

export async function startUnsynchronizedCoreRetry({ search = '', limit = 1000 } = {}) {
  const response = await fetch(`${API_BASE_URL()}/api/admin/unsynchronized-policies/retry-all`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ search: search || '', limit }),
  });
  const body = await response.json().catch(() => ({}));
  if (response.status === 409 && body.jobId) {
    return { ...body, status: body.status || 'running', alreadyRunning: true };
  }
  if (!response.ok) {
    throw new Error(body.error || body.message || 'Failed to start core retry');
  }
  return body;
}

export async function fetchUnsynchronizedCoreRetryStatus(jobId) {
  const response = await fetch(
    `${API_BASE_URL()}/api/admin/unsynchronized-policies/retry-all/status/${encodeURIComponent(jobId)}`,
    { headers: getAuthHeaders() }
  );
  return readJson(response, 'Failed to read core retry status');
}

export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);
}

export function formatNumber(num) {
  return new Intl.NumberFormat('en-KE').format(Number(num) || 0);
}

export function formatCompactCurrency(value) {
  const n = Number(value) || 0;
  if (n >= 1_000_000) return `Ksh ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `Ksh ${(n / 1_000).toFixed(0)}K`;
  return formatCurrency(n);
}
